<template>
  <div class="deep-preview-wrapper base-border-radius">
    <v-container v-if="loading" class="fill-height" fluid>
      <v-row class="align-center justify-center">
        <v-col cols="12" class="text-center">
          <v-progress-circular color="primary" width="2" indeterminate />
        </v-col>
      </v-row>
    </v-container>
    <DeepPreview
      v-if="src && mimeType"
      ref="preview"
      :src
      :mime-type
      pdf-true-scale
      pdf-worker-src="/pdf/pdf.worker.min.mjs"
      :pdf-go-to-page-padding="-5"
      @load="goToInitialPage()"
    >
      <template #actions>
        <FilePreviewActions
          v-if="preview?.isMimeTypeSupported"
          :is-downloading="loadingDownload"
          :num-pages="preview?.numPages"
          :current-page="preview?.currentPage"
          :zoom="preview?.getZoom()"
          :zoom-percentage="preview?.zoomPercentage"
          :hide-zoom="!isPdf && !isImage"
          :min-zoom="0.25"
          :max-zoom="2"
          :mime-type
          @click:download="downloadFile()"
          @click:zoom-out="preview?.zoomOut()"
          @click:zoom-in="preview?.zoomIn()"
          @click:print="preview?.print()"
        />
      </template>
      <template #unsupported>
        <FilePreviewDownload @click:btn="downloadFile()" />
      </template>
    </DeepPreview>
  </div>
</template>

<script setup lang="ts">
import { ref, useTemplateRef } from 'vue'
import { DeepPreview } from '@deepcloud/deep-preview'
import {
  downloadFileByUrlAndName,
  MIME_TYPES,
  MIME_TYPES_IMAGES,
} from '@deepcloud/deep-ui-utils'
import FilePreviewActions from './FilePreviewActions.vue'
import FilePreviewDownload from './FilePreviewDownload.vue'

const props = defineProps({
  filename: {
    type: String,
    required: true,
  },
  src: {
    type: String,
    required: true,
  },
  mimeType: {
    type: String,
    required: true,
  },
  page: {
    type: [Number, String],
    default: undefined,
  },
  loading: {
    type: Boolean,
    default: false,
  },
})

const preview = useTemplateRef('preview')

const loadingDownload = ref(false)

const isImage = computed(() => MIME_TYPES_IMAGES)
const isPdf = computed(() => props.mimeType === MIME_TYPES.MIME_TYPE_PDF)

async function goToInitialPage() {
  if (!props.page) return

  preview.value?.goToPage(props.page, { block: 'start' })
}

watch(() => props.page, goToInitialPage)

async function downloadFile() {
  loadingDownload.value = true
  try {
    downloadFileByUrlAndName(props.src, props.filename)
  } finally {
    loadingDownload.value = false
  }
}
</script>

<style lang="postcss">
@import '@deepcloud/deep-preview/dist/style.css';
</style>

<style scoped lang="postcss">
.deep-preview-wrapper {
  height: 100%;
  overflow: hidden;

  --dp-text-font-size: 14px;
  --dp-json-font-size: 14px;
}

/* this fixes an issue on some browser in some constellation that the video controls are not clickable */
:deep(.preview-video) {
  position: relative;
}
</style>
